import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import { deliveryStateValue, getDeliveryState, getDisplayState } from '@/services/db/delivery/utils';
import { capitalize } from '@/utils/string';
import { channelValueOf, getDeliveryChannel } from '../catalog/channels';
import { channelFormatterType, formatError } from '@/utils/format-error';
import { metaValue } from './delivery-row-state';

/** One label + timestamp entry in the expanded row's timestamp stack. */
export interface TimestampRow {
  key: string;
  label: string;
  timestamp: number;
}

/** Delivery-state labels that diverge from a plain capitalization. */
const DELIVERY_STATE_LABELS: Record<string, string> = {
  attempted: 'First attempted',
  deferred: 'Deferred for later sending',
};

/** User-action labels that diverge from a plain capitalization. */
const POST_DELIVERY_LABELS: Record<string, string> = {
  spammed: 'Reported spam',
  human_opened: 'Human opened',
  human_clicked: 'Human clicked',
  prefetch_opened: 'Machine opened',
  machine_clicked: 'Machine clicked',
};

function channelStates(delivery: Delivery): { deliveryStates: readonly string[]; deliveryActions: readonly string[] } {
  const channel = getDeliveryChannel(channelValueOf(delivery.action_type));
  return { deliveryStates: channel?.deliveryStates ?? [], deliveryActions: channel?.deliveryActions ?? [] };
}

function toRows(delivery: Delivery, states: readonly string[], label: (state: string) => string): TimestampRow[] {
  return states
    .map((state) => ({ key: state, label: label(state), timestamp: deliveryStateValue(delivery, state) }))
    .filter((row) => row.timestamp > 0);
}

/**
 * The delivery-lifecycle timestamps (attempted/sent/deferred/…), each shown as
 * a duration from `created`. `holdout` is dropped and `canceled` appended,
 * matching the model's `deliveryTimestamps`.
 */
export function deliveryTimestampRows(delivery: Delivery): TimestampRow[] {
  const states = [...channelStates(delivery).deliveryStates.filter((state) => state !== 'holdout'), 'canceled'];
  return toRows(delivery, states, (state) => DELIVERY_STATE_LABELS[state] ?? capitalize(state));
}

/**
 * The user-engagement timestamps (opened/clicked/converted/…), shown as
 * absolute times. Ember parity (`expansion` `postDeliveryTimestamps`): the
 * aggregate `opened`/`clicked` is dropped once a human/machine engagement
 * breakdown exists, to prioritize the human split. The breakdown rows
 * themselves (`human_opened`/`prefetch_opened`/…) are NOT rendered here — no
 * channel lists them in `deliveryActions`, and the data layer exposes them as
 * counts, not timestamps — exactly as in Ember's expansion. Showing the split
 * needs a timestamp source that doesn't exist yet (deferred, not a regression).
 */
export function postDeliveryActionRows(delivery: Delivery): TimestampRow[] {
  let actions = [...channelStates(delivery).deliveryActions];
  if (deliveryStateValue(delivery, 'human_opened') > 0 || deliveryStateValue(delivery, 'prefetch_opened') > 0) {
    actions = actions.filter((action) => action !== 'opened');
  }
  if (deliveryStateValue(delivery, 'human_clicked') > 0 || deliveryStateValue(delivery, 'machine_clicked') > 0) {
    actions = actions.filter((action) => action !== 'clicked');
  }
  return toRows(delivery, actions, (state) => POST_DELIVERY_LABELS[state] ?? capitalize(state));
}

/** States whose `meta.reason` surfaces as an informational reason line. */
const REASON_STATES = new Set(['failed', 'bounced', 'suppressed', 'attempted', 'undeliverable']);

/**
 * The reason region of the timestamp stack — mutually exclusive with the
 * user-action rows:
 * - `errors`: a failed delivery with error detail (from the fetched detail's
 *   `error_messages`, else the delivery's own error message).
 * - `reason`: a non-failed terminal state exposing a `meta.reason`.
 * - `none`: neither — the caller falls back to the user-action rows.
 */
export type ReasonView =
  | { kind: 'errors'; messages: string[] }
  | { kind: 'reason'; message: string }
  | { kind: 'none' };

function deliveryErrorMessage(delivery: Delivery): string | undefined {
  const failure = delivery.failure_message;
  if (failure != null && failure !== '') {
    return failure;
  }
  return metaValue(delivery, 'reason') ?? metaValue(delivery, 'failed_message');
}

/** Push deliveries encode per-device statuses as JSON in `meta.reason`. */
function hasDeviceStatuses(delivery: Delivery): boolean {
  if (delivery.action_type !== 'push_action') {
    return false;
  }
  const reason = metaValue(delivery, 'reason');
  if (reason == null) {
    return false;
  }
  try {
    const parsed: unknown = JSON.parse(reason);
    return parsed != null && typeof parsed === 'object';
  } catch {
    return false;
  }
}

export function deliveryReason(delivery: Delivery, detail: DeliveryDetail | undefined): ReasonView {
  const detailErrors = detail?.error_messages ?? [];
  const errorMessage = deliveryErrorMessage(delivery);
  const hasError = errorMessage != null || detailErrors.length > 0;

  // Channel + delivery state drive the message map (push codes, twilio codes);
  // unknown channels fall back to the raw string.
  const type = channelFormatterType(delivery.action_type);
  const deliveryState = getDeliveryState(delivery) ?? null;

  if (deliveryState === 'failed' && hasError && !hasDeviceStatuses(delivery)) {
    const raw = detailErrors.length > 0 ? detailErrors : errorMessage != null ? [errorMessage] : [];
    return { kind: 'errors', messages: raw.map((message) => formatError(message, { type, deliveryState })) };
  }

  const displayState = getDisplayState(delivery);
  const reason = metaValue(delivery, 'reason');
  if (displayState != null && REASON_STATES.has(displayState) && reason != null) {
    return { kind: 'reason', message: formatError(reason, { type, deliveryState }) };
  }

  return { kind: 'none' };
}
